import { useEffect, useRef, useState } from "react";

import { SiteNavSectionKey } from "./siteNavConfig";

const OPEN_DELAY_MS = 80;
const CLOSE_DELAY_MS = 120;

/*
  Hover previews a section. Opening waits a beat so sweeping the pointer across
  the nav doesn't flash panels; once one is open, moving to another section
  swaps instantly. `last` keeps the content while the panel fades out.
*/
export const useSectionHover = () => {
  const [hovered, setHovered] = useState<SiteNavSectionKey | null>(null);
  const [last, setLast] = useState<SiteNavSectionKey | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hoveredRef = useRef(hovered);
  hoveredRef.current = hovered;

  useEffect(() => () => clearTimeout(timer.current), []);

  const show = (key: SiteNavSectionKey) => {
    clearTimeout(timer.current);
    const apply = () => {
      setHovered(key);
      setLast(key);
    };
    if (hoveredRef.current) apply();
    else timer.current = setTimeout(apply, OPEN_DELAY_MS);
  };

  const showNow = (key: SiteNavSectionKey) => {
    clearTimeout(timer.current);
    setHovered(key);
    setLast(key);
  };

  const scheduleClose = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setHovered(null), CLOSE_DELAY_MS);
  };

  const cancelClose = () => clearTimeout(timer.current);

  const close = () => {
    clearTimeout(timer.current);
    setHovered(null);
  };

  return { hovered, last, show, showNow, scheduleClose, cancelClose, close };
};
